import { ChangeEvent, on } from '@disy/lib/event-util';
import type { Nullable } from '@disy/lib/type-util';
import { getUrlParam } from '@disy/lib/url-param-util';
import type { IconOptions } from '@disy/ui/icon';

import { isEmbeddedView } from 'cadenza/integration/cadenza-api-util';
import type { WorkbookMapView } from 'cadenza/map/custom/workbook/workbook-map-view';
import { getStore } from 'cadenza/store/cadenza-store';
import type { PartialContext } from 'cadenza/ui/link/model/partial-context.gen';
import { visualizationIcon } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { viewTypeIcon } from 'cadenza/workbook/api/view-type';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type { EmbeddedView } from 'cadenza/workbook/view/embedded-view';
import type { View } from 'cadenza/workbook/view/view';
import {
  getActiveViewId,
  getDataViewById,
  getViewByDataViewId,
  getWorkbookPrivileges,
  isCombinationChart,
  isMaximizedView,
  isReportMode,
  isWorkbookVisibleRestricted,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { activateView, minimizeView } from 'cadenza/workbook/workbook.state';
import { isLinearLayout } from 'cadenza/workbook/worksheet-layout';

export function getMap(view: View) {
  return getMapView(view).workbookMap.map;
}

export function getMapView(view: View) {
  // view may have multiple map views if it has integrated overview map.
  // Then we need to get the actual map, not the overview map
  return findMapViewInView(view)!;
}

function findMapViewInView(view: Nullable<View>) {
  if (view) {
    return [...view.querySelectorAll<WorkbookMapView>('.map')].find((v) => v.viewId === view.viewId);
  }
}

export function hasExplicitHeight() {
  return (!isLinearLayout() || isEmbeddedView()) && !isReportMode(getStore().getState());
}

export function isLinearLayoutForbidden() {
  const partialContext = getUrlParam<PartialContext>('partialContext');
  return (
    !document.querySelector('.d-workbook-page') ||
    document.querySelector('.d-workbook-embedded-preview') ||
    document.querySelector('.print-layout--main') ||
    partialContext === 'viewEmbedding' ||
    partialContext === 'viewPopup'
  );
}

const IS_INERT_CLASS = 'is-inert';

/**
 * Adds/removes 'is-inert' class from view element.
 * This class indicates the ancestor d-worksheet-view that it should trigger its 'inert' attribute
 * in order to avoid keyboard navigation through this view if empty (e.g. display condition is off).
 * @param embeddedView - the view element
 * @param isValid - whether the view has a valid state for displaying and interacting
 * @private
 */
export function toggleIsInertClass(embeddedView: EmbeddedView, isValid?: boolean) {
  const userPrivileges = getWorkbookPrivileges(getStore().getState());
  if (userPrivileges.length === 0 && isWorkbookVisibleRestricted(getStore().getState())) {
    embeddedView.classList.toggle(IS_INERT_CLASS, !isValid);
    embeddedView.dispatchEvent(new ChangeEvent());
  }
}

export function toggleViewInertOnContentChange(embeddedView: EmbeddedView, view: View) {
  on(embeddedView, 'change', () => {
    if (embeddedView.classList.contains(IS_INERT_CLASS)) {
      view.setAttribute('inert', 'true');
    } else {
      view.removeAttribute('inert');
    }
  });
}

export function activateViewOnIframeFocus(iframe: HTMLIFrameElement, viewId: ViewId) {
  return window.setInterval(() => checkFocus(iframe, viewId), 100);
}

/**
 * CADENZA-30166: Because we want to activate an extensionView if it was clicked inside the view
 * and the default click- and other event-handlers of the view.ts doesn't recognize that
 * (because the iFrame displays content from another origin) we need a workaround solution.
 * After a discussion in the webDev guild now the setInterval solution proposed in
 * https://stackoverflow.com/a/42373631/6650315 was adapted to our code
 */
function checkFocus(iframe: HTMLIFrameElement, viewId: ViewId) {
  if (window.document.activeElement === iframe && viewId !== getActiveViewId(getStore().getState())) {
    getStore().dispatch(activateView(viewId));
  }
}

export function getViewIcon(view: ViewDto, options?: IconOptions) {
  return view.dataViews.length === 1 ?
      visualizationIcon(getDataViewById(getStore().getState(), view.dataViews[0]).visualizationKey, options)
    : viewTypeIcon(isCombinationChart(getStore().getState(), view.id) ? 'CHART_COMBINATION' : view.type, options);
}

export function minimizeViewIfMaximized(dataViewId: DataViewId) {
  const viewId = getViewByDataViewId(getStore().getState(), dataViewId).id;
  if (isMaximizedView(getStore().getState(), viewId)) {
    getStore().dispatch(minimizeView());
  }
}
